import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";

export default function Korporativ() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Корпоратив в ресторане Петербург — Паруса на крыше",
    description: "Корпоратив на крыше в центре Петербурга. Залы до 300 гостей, профессиональная сцена и звук, индивидуальное меню. Организация под ключ. +7 (812) 991-10-90.",
    keywords: "корпоратив ресторан Петербург, корпоратив на крыше СПб, аренда зала корпоратив Петербург, корпоративное мероприятие ресторан, новый год корпоратив СПб, корпоратив с панорамным видом",
    image: "/photos/view_clouds.jpg",
    canonical: "/korporativ",
  });
  useBreadcrumbs([{ name: "Корпоратив", path: "/korporativ" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <div className="w-full bg-background">
      {/* Hero */}
      <section className="relative h-[80vh] w-full flex items-center justify-center overflow-hidden border-b border-primary/20">
        <div className="absolute inset-0 z-0">
          <img src="/photos/view_clouds.webp" alt="Корпоратив на крыше Петербурга" className="w-full h-full object-cover" style={{ filter: "brightness(0.22) saturate(0.5)", transform: "scale(1.05)" }} loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(5,20,10,0.95) 0%, rgba(10,30,18,0.88) 100%)" }} />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(30px)", transition: "all 1s ease 0.3s" }}>
          <div className="inline-block px-6 py-2 border border-primary/30 mb-8">
            <span className="text-[9px] tracking-[0.3em] uppercase text-accent">Корпоратив</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-6 leading-[1.1]">
            Корпоратив<br /><span className="text-accent italic">на крыше в Петербурге</span>
          </h1>
          <p className="text-white/60 text-lg font-light mb-10 max-w-xl mx-auto leading-relaxed">
            Залы до 300 гостей, профессиональная сцена и панорама всего Петербурга
          </p>
          <button onClick={openBookingModal} className="bg-primary/20 border border-primary text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary hover:text-background transition-colors">
            Запросить расчёт
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "300", label: "Гостей максимум" },
              { value: "6", label: "Этаж Olympic Plaza" },
              { value: "300°", label: "Панорамный обзор" },
              { value: "15+", label: "Лет на рынке" },
            ].map((s) => (
              <div key={s.label} className="text-center border-r border-primary/10 last:border-0 px-4">
                <div className="font-serif text-4xl md:text-5xl font-light text-primary mb-2">{s.value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-32 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Почему Паруса</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-8 leading-tight">
                Ваша команда<br /><span className="text-accent italic">заслуживает большего</span>
              </h2>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                Корпоратив на высоте — в прямом смысле. Профессиональная сцена, мощная звуковая система, бесперебойное обслуживание и авторская кухня для компании любого размера.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                {[
                  { label: "До 300 гостей", sub: "Вместимость залов" },
                  { label: "Сцена и звук", sub: "Профессиональное оборудование" },
                  { label: "Меню под заказ", sub: "Индивидуальный кейтеринг" },
                  { label: "Организация", sub: "Полностью под ключ" },
                ].map((item) => (
                  <div key={item.label} className="border-l border-primary/30 pl-4">
                    <div className="text-accent text-sm font-medium mb-1">{item.label}</div>
                    <div className="text-muted-foreground text-xs font-light">{item.sub}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img src="/photos/view_summer.webp" alt="Корпоратив с видом на Петербург" className="w-full aspect-[4/3] object-cover border border-primary/15" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Zones */}
      <section className="py-32 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-16">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Площадки</div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground">Выберите формат</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "Зона у сцены", sub: "до 45 гостей", desc: "Лучшие места у сцены — для небольшой компании, которая хочет быть в центре событий.", price: "от 280 000 ₽ / 4 часа" },
              { num: "02", title: "Банкетный зал", sub: "2 этаж", desc: "Отдельный зал для корпоратива любого масштаба с полным закрытием и индивидуальным обслуживанием.", price: "от 350 000 ₽" },
              { num: "03", title: "Полное закрытие", sub: "до 300 гостей", desc: "Весь ресторан для вашей команды — с открытой террасой, сценой и панорамой Петербурга.", price: "от 2 000 000 ₽" },
            ].map((z) => (
              <div key={z.num} className="border border-primary/20 p-10 bg-background group hover:border-primary/40 transition-colors">
                <div className="font-serif text-5xl font-light text-primary/15 mb-5 group-hover:text-primary/35 transition-colors">{z.num}</div>
                <h3 className="font-serif text-2xl font-light text-foreground mb-1">{z.title}</h3>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground/50 mb-4">{z.sub}</div>
                <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">{z.desc}</p>
                <div className="text-[10px] tracking-[0.2em] uppercase text-primary/60 border-t border-primary/15 pt-4">{z.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqSection items={PAGE_FAQS["/korporativ"]} title="Вопросы о корпоративе" />

      {/* CTA */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <h2 className="text-4xl font-serif font-light text-foreground mb-6">Обсудим ваш корпоратив</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">
            Оставьте заявку — менеджер по мероприятиям свяжется с вами и подберёт оптимальный формат.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openBookingModal} className="bg-primary border border-primary text-background px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary/80 transition-colors">
              Запросить расчёт
            </button>
            <a href="/banket#banquet-form" className="border border-primary/40 text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:border-primary transition-colors">
              Оставить заявку
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
